import { formatCurrency, formatDate } from "@/common/utils/formatters";
import { AlertCircleIcon, ArrowBigRight } from "lucide-react";
import { BillData } from "../types/bills.type";
import { Link } from "react-router-dom";
import { billingBillStatus } from "@/common/constants/statusBills.constants";
import BillDateWithTimezone from "@/common/components/BillDateWithTimezone";
import { Tooltip } from "react-tooltip";
import Button from "@/common/components/Button";
import { companySubGroupableKlass } from "@/common/constants/companySubGroupableKlass.constant";
import { formatDatePeriodFriendlyName } from "@/common/utils/dateTransformer";

export class BillsTransformer {
  static tableTransformer() {
    return (bills: BillData[]) => {
      return bills.map((bill) => ({
        ...bill,
        period: formatDatePeriodFriendlyName({
          contractualCycle: bill.contractual_cycle,
          openedAt: bill.opened_at,
          closedAt: bill.closed_at,
        }),
        status: (
          <div className="flex items-center gap-2">
            <p>
              {bill.status ? billingBillStatus.find(({ value }) => value === bill?.status)?.label : ''}
            </p>

            {bill.rejected_by_company_at && (
              <>
                <AlertCircleIcon
                  strokeWidth={1.5}
                  className="text-red-500 size-4"
                  data-tooltip-id="bill-rejected-tooltip"
                />

                <Tooltip id="bill-rejected-tooltip" className="text-sm z-50">
                  Fatura rejeitada
                </Tooltip>
              </>
            )}
          </div>
        ),
        groupable_display_name: (
          <p>
            {bill.grouping_mode !== 'by_government_allocation' ? bill.groupable?.display_name || 'Nenhum' : 'Empenho'}
            {bill?.sub_groupable_klass && (
              <span className="italic">
                &nbsp; - &nbsp;
                {companySubGroupableKlass[bill?.sub_groupable_klass]}
              </span>
            )}
          </p>
        ),
        company_branch: bill.client_branch.display_name || '',
        movements_total_in_bill: bill.movements_total_in_bill ? formatCurrency(parseFloat(bill.movements_total_in_bill)) : '--',
        credit_info: bill?.granted_credit ? formatCurrency(parseFloat(`${bill?.granted_credit}`)) : undefined,
        total_amount: bill?.total_amount ? formatCurrency(parseFloat(`${bill?.total_amount}`)) : '',
        events_count: `${bill.events_count || 0} transações`,
        opened_at: bill.opened_at && (
          <BillDateWithTimezone
            date={bill.opened_at}
            timezone={bill.time_zone}
          />
        ),
        closed_at: bill.closed_at && (
          <BillDateWithTimezone
            date={bill.closed_at}
            timezone={bill.time_zone}
          />
        ),
        due_at: bill?.due_at && (
          <BillDateWithTimezone
            date={bill.due_at}
            timezone={{
              id: 'america_sao_paulo',
              acronym: 'BRT',
              display_name: 'América - São Paulo',
              identifier: 'America/Sao_Paulo',
              offset: '-03:00',
            }}
            hideTimezone
          />
        ),
        actions: (
          <Link to={`/billing-bills/${bill.id}`}>
            <Button
            variant="link-white"
            className="w-full"
            operationKlassToCheckPermission="Client::Billing::Bills::Show"
            >
              <ArrowBigRight />
            </Button>
          </Link>
        ),
      }));
    };
  }

  static quickSearchModelTransformer() {
    return (bills: BillData[]) => {
      return bills.map((bill) => ({
        route: `/billing-bills/${bill.id}`,
        items: [
          ['Status', billingBillStatus.find((option) => option.value === bill.status)?.label || ''],
          ['Código', bill.number],
          ['Abertura', bill.opened_at && formatDate(new Date(bill.opened_at))],
          ['Fechamento', bill.closed_at && formatDate(new Date(bill.closed_at))],
        ],
      }));
    };
  }
}
